/**
 * Vocabulario canónico del contrato — US-26 (gestión de usuarios y roles).
 *
 * Fuente: decisión de Guo (2026-07-07) — enum de rol `usuario|champion|admin`.
 * El `OWNER` del tenant fundador se mapea a `admin`. Estos valores son los que
 * viajan en el descriptor (`user.rol`) y en las peticiones/respuestas de los
 * endpoints de administración; front y kernel DEBEN usar exactamente los mismos.
 *
 * Vive fuera de `mock/` a propósito: es contrato, no dato de mock. Lo consumen
 * tanto el mock como los componentes reales.
 */

// Roles asignables a un empleado del tenant (US-26).
export const ROLES = ['usuario', 'champion', 'admin']

// Estado de un empleado: invitación pendiente, activo o de baja (deshabilitado en Cognito).
export const ESTADOS = ['pendiente', 'activo', 'baja']

export const ROLE_LABELS = {
  usuario:  'Usuario',
  champion: 'Champion',
  admin:    'Administrador',
}

export const ESTADO_LABELS = {
  pendiente: 'Pendiente',
  activo:    'Activo',
  baja:      'De baja',
}

// ---------------------------------------------------------------------------
// Versionado del contrato — US-68 (#202)
// Fuente: 02-contrato-descriptor-sesion.md §6 (versionado).
//
// El front declara aquí la versión de contrato contra la que está construido.
// El descriptor de bootstrap del kernel trae su propia `contract_version`; el
// front las compara para no asumir compatibilidad a ciegas cuando el kernel
// publique una versión nueva.
//
// Regla (§6, versionado semver): los cambios MENORES del contrato son aditivos
// y compatibles hacia atrás, por lo que mismo MAJOR ⇒ compatible. Un MAJOR
// distinto rompe compatibilidad: el front degrada con aviso (no crash).
// ---------------------------------------------------------------------------

// Versión de contrato contra la que está construido este front (hoy v1.0).
export const CONTRACT_VERSION = '1.0'

// Extrae el componente MAJOR de una versión "MAJOR.MINOR" (o "MAJOR").
// Devuelve null si no es una cadena con un major numérico reconocible.
function majorOf(version) {
  if (typeof version !== 'string') return null
  const major = parseInt(version.trim(), 10)
  return Number.isNaN(major) ? null : major
}

/**
 * Compara la `contract_version` del descriptor del kernel con la declarada por
 * el front. No lanza: siempre devuelve un veredicto que la UI puede pintar.
 *
 * Estados:
 *  - 'ok'          : mismo MAJOR (compatible; los cambios menores son aditivos).
 *  - 'unsupported' : MAJOR distinto — el kernel habla un contrato que este
 *                    front no soporta (adelantado o atrasado). Se degrada con
 *                    aviso, no se asume compatibilidad.
 *  - 'unknown'     : el kernel no declara versión o no es parseable. No se
 *                    puede verificar; se avisa pero no se bloquea el arranque.
 *
 * `ahead` indica, para 'unsupported', si el kernel va por delante (MAJOR mayor)
 * o por detrás (MAJOR menor) del front.
 */
export function checkContractCompatibility(received) {
  const declared = CONTRACT_VERSION
  const declaredMajor = majorOf(declared)
  const receivedMajor = majorOf(received)

  if (receivedMajor === null) {
    return { status: 'unknown', declared, received: received ?? null }
  }
  if (receivedMajor === declaredMajor) {
    return { status: 'ok', declared, received }
  }
  return {
    status: 'unsupported',
    declared,
    received,
    ahead: receivedMajor > declaredMajor,
  }
}

// Mensaje legible para el usuario cuando la compatibilidad no es 'ok'.
// Devuelve null para 'ok' (no hay nada que avisar).
export function contractWarningMessage(compat) {
  if (!compat || compat.status === 'ok') return null
  if (compat.status === 'unknown') {
    return 'No se pudo verificar la versión del contrato con el servidor. ' +
           'Algunas funciones podrían no comportarse como se espera.'
  }
  // unsupported
  return compat.ahead
    ? `El servidor usa una versión del contrato (${compat.received}) más nueva ` +
      `que esta aplicación (${compat.declared}). Puede que algunas funciones no ` +
      `estén disponibles hasta actualizar Pórtico.`
    : `El servidor usa una versión del contrato (${compat.received}) más antigua ` +
      `que esta aplicación (${compat.declared}). Puede que algunas funciones no ` +
      `estén disponibles.`
}
